import { useMemo } from "react";
import TopSellers from "../../components/TopSellers";
import CustomTitle from "../../components/CustomTitle";
import Footer from "../../components/layouts/Footer";
import MailingList from "../../components/MailingList";
import LoginOrRegisterBanner from "../../components/LoginOrRegisterBanner";
import SwipperMultiple from "../../components/SwipperMultiple";
import { ArtworkCardProps } from "../../types";
import { usePersistStore, useStore } from "../../store";
import { AD_SORTING, AD_TYPES } from "../../utils/enums";
import RecentArtworksSwipper from "../../components/RecentArtworksSwipper";
import SetNewPassword from "../../components/SetNewPassword";
import ArtworkCardSkeletonRow from "../../components/ArtworkCardSkeletonRow";
import CategoryChips from "../../components/CategoryChips";
import { useAdSearch, useFollowedAds } from "../../hooks/queries";

// Map a raw ad into the ArtworkCard props shape shared by the home sections.
const toCard = (tt: any, color: boolean): ArtworkCardProps => ({
  _id: tt?._id,
  color,
  imageUrl: tt?.images?.[0],
  category: tt?.categoryId?.name,
  subCategory: tt?.subCategoryId?.name,
  views: tt?.views || 0,
  title: tt?.title,
  ownerImage: tt?.userId?.fileUrl,
  ownerName: tt?.userId?.name,
  ownerId: tt?.userId?._id,
  biddingStarts: tt?.biddingStarts,
  biddingEnds: tt?.biddingEnds,
  price: tt?.price,
  showBidNowButton: true,
  status: tt?.status,
  distance: tt?.distance,
});

function Home() {
  const {
    keyword,
    priceRange,
    radius,
    mediumIds,
    isGoogleSignup,
    setIsGoogleSignup,
  } = useStore((s: any) => s);
  const { token, user, currentLocation } = usePersistStore((s: any) => s);

  // Shared search body; only `type` differs per section.
  const baseBody = {
    page: 0,
    limit: 5,
    sorting: AD_SORTING.PRICE_LOW_TO_HIGH,
    keyword,
    range: priceRange,
    radius,
    mediumIds,
    location: currentLocation,
  };
  const q1 = useAdSearch({ ...baseBody, type: AD_TYPES.OPEN_FOR_BIDS });
  const q2 = useAdSearch({ ...baseBody, type: "popular" });
  const q3 = useAdSearch({ ...baseBody, type: AD_TYPES.UPCOMING });
  // Ads from sellers the signed-in user follows. Fetched only when a token is
  // present; the section is hidden when empty so users who haven't followed
  // anyone don't see a dead card.
  const q4 = useFollowedAds(token, {
    type: AD_TYPES.OPEN_FOR_BIDS,
    page: 0,
    limit: 5,
    location: currentLocation,
  });

  const list1 = useMemo(
    () => (q1.data?.ads || []).map((tt: any) => toCard(tt, true)),
    [q1.data]
  );
  const list2 = useMemo(
    () => (q2.data?.ads || []).map((tt: any) => toCard(tt, false)),
    [q2.data]
  );
  const list3 = useMemo(
    () => (q3.data?.ads || []).map((tt: any) => toCard(tt, false)),
    [q3.data]
  );
  const list4 = useMemo(
    () => (q4.data?.ads || []).map((tt: any) => toCard(tt, true)),
    [q4.data]
  );
  const isReady1 = !q1.isLoading;
  const isReady2 = !q2.isLoading;
  const isReady3 = !q3.isLoading;
  const isReady4 = !q4.isLoading;

  return (
    <>
      {isGoogleSignup && (
        <SetNewPassword
          show={isGoogleSignup}
          onHide={() => setIsGoogleSignup(false)}
        />
      )}
      <div className="container px-md-0 px-3">
        <div className="row gx-md-0">
          <div className="col-md-12 px-md-0 px-3 mt-md-4 mt-3">
            <RecentArtworksSwipper />
          </div>
          <div className="col-md-12 px-md-0 px-3 mt-4">
            <CategoryChips />
          </div>
        </div>
      </div>
      {token && list4?.length > 0 && (
        <div className="mt-5 position-relative">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                kicker="From artists you follow"
                title="Art from sellers you follow"
                link="/followed-sellers"
                showAll={true}
              />
            </div>
            {!isReady4 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple
                uniqueId="sellersYouFollow"
                slidesData={list4}
              />
            )}
          </div>
        </div>
      )}
      {(!isReady2 || list2?.length > 0) && (
        <div className="mt-5 position-relative themedSection themedSection--cream">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                kicker="Trending now"
                title="Popular bids"
                link="/explore/popular"
                showAll={true}
              />
            </div>
            {!isReady2 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple
                uniqueId="popularBids"
                slidesData={list2}
                showFireEmoji={true}
              />
            )}
          </div>
        </div>
      )}
      {!token && !user && (
        <div className="col-md-10 mx-auto mt-5">
          <LoginOrRegisterBanner />
        </div>
      )}
      <div className="mt-5 position-relative">
        <div className="container py-4 px-md-0 px-3">
          <div className="py-4">
            <CustomTitle
              kicker="Featured"
              title="This week's featured artists"
              link="sellers"
              showAll={true}
            />
          </div>
          <div className="row justify-content-start">
            <TopSellers />
          </div>
        </div>
      </div>

      {(!isReady1 || list1?.length > 0) && (
        <div className="mt-5 position-relative">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                kicker="Open now"
                title="Open for bid"
                link="/explore/open-for-bids"
                showAll={true}
              />
            </div>
            {!isReady1 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple uniqueId="openForBids" slidesData={list1} />
            )}
          </div>
        </div>
      )}
      {(!isReady3 || list3?.length > 0) && (
        <div className="mt-5 position-relative themedSection themedSection--cream">
          <div className="container py-4 px-md-0 px-3">
            <div className="py-4">
              <CustomTitle
                kicker="Coming soon"
                title="Upcoming bids"
                link="/explore/upcoming"
                showAll={true}
              />
            </div>
            {!isReady3 ? (
              <ArtworkCardSkeletonRow />
            ) : (
              <SwipperMultiple uniqueId="upcomingBids" slidesData={list3} />
            )}
          </div>
        </div>
      )}

      {/* <div className="mt-5 position-relative">
        <div className="container py-4 px-md-0 px-3">
          <div className="py-4">
            <CustomTitle
              title="What people are saying"
              centered={true}
              showAll={false}
              link="#"
            />
          </div>
          <Testimonials uniqueId="testimonials" slidesData={testimonials} />
        </div>
      </div> */}
      {/* <div className="container pt-4">
        <div className="row mx-0">
          <div className="col-md-11 mx-auto mb-4">
            <MailingList />
          </div>
        </div>
      </div> */}
      <Footer />
    </>
  );
}

export default Home;
